import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api, callableErrorMessage, type GeofenceLocation } from "../lib/api";

type Props = { tenantId: string };

export function GeofenceEditor({ tenantId }: Props) {
  const [locations, setLocations] = useState<GeofenceLocation[]>([]);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(doc(db, "tenants", tenantId, "config", "geofence"), (snap) => {
      const d = snap.data();
      if (d?.locations) setLocations(d.locations as GeofenceLocation[]);
    });
  }, [tenantId]);

  const addCurrentLocation = () => {
    if (!navigator.geolocation) {
      setFeedback("Tu navegador no soporta geolocalizacion.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const name = prompt("Nombre del local (ej: Sucursal Centro)") ?? "";
        if (!name.trim()) return;
        setLocations([
          ...locations,
          { name: name.trim(), lat: pos.coords.latitude, lng: pos.coords.longitude, radiusMeters: 100 }
        ]);
      },
      (err) => setFeedback("No pudimos detectar tu ubicacion: " + err.message),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const addManual = () => {
    const name = prompt("Nombre del local")?.trim();
    if (!name) return;
    const latStr = prompt("Latitud (ej: -33.4172)")?.trim();
    const lngStr = prompt("Longitud (ej: -70.6062)")?.trim();
    const lat = Number(latStr);
    const lng = Number(lngStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      alert("Coordenadas invalidas.");
      return;
    }
    setLocations([...locations, { name, lat, lng, radiusMeters: 100 }]);
  };

  const remove = (idx: number) => {
    setLocations(locations.filter((_, i) => i !== idx));
  };

  const updateRadius = (idx: number, radius: number) => {
    setLocations(locations.map((l, i) => (i === idx ? { ...l, radiusMeters: radius } : l)));
  };

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await api.updateTenantGeofence({ tenantId, locations });
      setFeedback(`Guardado: ${res.data.count} locales.`);
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {locations.length === 0 && (
        <p className="rounded-xl bg-leaf-50 px-3 py-2 text-xs text-leaf-700">
          Sin locales definidos. Los clientes pueden cobrar desde cualquier parte.
        </p>
      )}

      {locations.map((l, i) => (
        <div key={i} className="rounded-2xl bg-leaf-50/50 p-3 ring-1 ring-leaf-100">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-leaf-950">{l.name}</p>
              <p className="font-mono text-[10px] text-leaf-500">{l.lat.toFixed(5)}, {l.lng.toFixed(5)}</p>
            </div>
            <button
              onClick={() => remove(i)}
              className="text-xs text-red-700 hover:underline"
            >
              Quitar
            </button>
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-leaf-700">
            Radio
            <input
              type="number"
              min={30}
              max={5000}
              value={l.radiusMeters}
              onChange={(e) => updateRadius(i, Number(e.target.value))}
              className="input w-24 py-1 text-xs"
            />
            metros
          </label>
        </div>
      ))}

      <div className="grid grid-cols-2 gap-2">
        <button onClick={addCurrentLocation} className="btn-secondary text-xs">
          Usar mi ubicacion
        </button>
        <button onClick={addManual} className="btn-secondary text-xs">
          Ingresar coords
        </button>
      </div>

      {feedback && (
        <p className="rounded-xl bg-leaf-50 px-3 py-2 text-xs text-leaf-800">{feedback}</p>
      )}

      <button onClick={save} disabled={busy} className="btn-primary">
        {busy ? "Guardando…" : `Guardar ${locations.length} local(es)`}
      </button>
    </div>
  );
}
